import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, MousePointerClick, Mail, Send, TrendingUp,
  ChevronDown, ChevronUp, User, FileText,
  Eye, RefreshCw, AlertTriangle, Globe, ShieldAlert
} from 'lucide-react';
import Card from '../components/common/Card';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import SearchBar from '../components/common/SearchBar';
import Toast from '../components/common/Toast';
import { getDeliveryStats, getDeliveries } from '../services/deliveryAuditService';
import './DeliveryAudit.css';

const TABS = [
  { key: 'overview', label: 'Overview', icon: <BarChart3 size={18} /> },
  { key: 'deliveries', label: 'All Deliveries', icon: <Send size={18} /> },
  { key: 'reports', label: 'By Report', icon: <FileText size={18} /> },
  { key: 'recipients', label: 'By Recipient', icon: <User size={18} /> },
];

const DAYS_OPTIONS = [1, 2, 7, 14, 30, 60, 90];

export default function DeliveryAudit() {
  const [activeTab, setActiveTab] = useState('overview');
  const [daysBack, setDaysBack] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Data
  const [stats, setStats] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState('');
  const [expandedReport, setExpandedReport] = useState(null);
  const [expandedRecipient, setExpandedRecipient] = useState(null);
  const [expandedDelivery, setExpandedDelivery] = useState(null);

  /** Compute unique IPs from the client_ips array on a delivery record */
  const getUniqueIps = (delivery) => {
    const ips = delivery.client_ips || [];
    return [...new Set(ips)];
  };

  // Debounce search term to avoid re-fetching on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDeliveryStats(daysBack);
      setStats(data);
    } catch (err) {
      setError(err.message || 'Failed to load delivery stats');
    } finally {
      setLoading(false);
    }
  }, [daysBack]);

  const fetchDeliveries = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDeliveries({
        page,
        pageSize: 50,
        search: debouncedSearch,
        status: statusFilter,
        deliveryType: deliveryTypeFilter,
        daysBack
      });
      setDeliveries(data.deliveries);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  }, [daysBack, debouncedSearch, statusFilter, deliveryTypeFilter]);

  useEffect(() => {
    if (activeTab === 'overview' || activeTab === 'reports' || activeTab === 'recipients') {
      fetchStats();
    } else if (activeTab === 'deliveries') {
      fetchDeliveries();
    }
  }, [activeTab, fetchStats, fetchDeliveries]);

  const handleRefresh = () => {
    if (activeTab === 'deliveries') fetchDeliveries();
    else fetchStats();
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatTimeAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  // ── Overview Tab ──
  const renderOverview = () => {
    if (!stats) return null;
    const { summary, reports } = stats;

    return (
      <div className="da-overview">
        <div className="da-stats-grid">
          <Card className="da-stat-card" hoverable>
            <div className="da-stat-icon da-stat-sent"><Send size={24} /></div>
            <div className="da-stat-content">
              <p className="da-stat-label">Emails Sent</p>
              <p className="da-stat-value">{summary.total_deliveries}</p>
            </div>
          </Card>
          <Card className="da-stat-card" hoverable>
            <div className="da-stat-icon da-stat-clicked"><MousePointerClick size={24} /></div>
            <div className="da-stat-content">
              <p className="da-stat-label">Recipients Clicked</p>
              <p className="da-stat-value">{summary.total_clicked}</p>
            </div>
          </Card>
          <Card className="da-stat-card" hoverable>
            <div className="da-stat-icon da-stat-clicks"><TrendingUp size={24} /></div>
            <div className="da-stat-content">
              <p className="da-stat-label">Total Clicks</p>
              <p className="da-stat-value">{summary.total_clicks}</p>
            </div>
          </Card>
          <Card className="da-stat-card" hoverable>
            <div className="da-stat-icon da-stat-rate"><Eye size={24} /></div>
            <div className="da-stat-content">
              <p className="da-stat-label">Open Rate</p>
              <p className="da-stat-value">{summary.open_rate}%</p>
            </div>
          </Card>
          {(summary.total_bounced > 0 || summary.total_complained > 0) && (
            <Card className="da-stat-card da-stat-clickable" hoverable clickable onClick={() => { setActiveTab('deliveries'); setStatusFilter('bounced'); }}>
              <div className="da-stat-icon da-stat-bounced"><AlertTriangle size={24} /></div>
              <div className="da-stat-content">
                <p className="da-stat-label">Bounced</p>
                <p className="da-stat-value">{summary.total_bounced || 0}</p>
              </div>
            </Card>
          )}
          {summary.total_complained > 0 && (
            <Card className="da-stat-card da-stat-clickable" hoverable clickable onClick={() => { setActiveTab('deliveries'); setStatusFilter('complained'); }}>
              <div className="da-stat-icon da-stat-complained"><AlertTriangle size={24} /></div>
              <div className="da-stat-content">
                <p className="da-stat-label">Complaints</p>
                <p className="da-stat-value">{summary.total_complained}</p>
              </div>
            </Card>
          )}
        </div>

        {/* Open rate bar */}
        <Card className="da-open-rate-card">
          <h3 className="da-section-title">Open Rate</h3>
          <div className="da-progress-bar">
            <div className="da-progress-fill" style={{ width: `${Math.min(summary.open_rate, 100)}%` }} />
          </div>
          <p className="da-progress-label">
            {summary.total_clicked} of {summary.total_deliveries} recipients opened their report
          </p>
        </Card>

        {/* Report breakdown table */}
        <Card className="da-report-breakdown">
          <h3 className="da-section-title">Report Breakdown</h3>
          {reports.length === 0 ? (
            <p className="da-empty">No delivery data in the last {daysBack} days</p>
          ) : (
            <div className="da-table-wrapper">
              <table className="da-table">
                <thead>
                  <tr>
                    <th>Report</th>
                    <th>Sent</th>
                    <th>Clicked</th>
                    <th>Bounced</th>
                    <th>Open Rate</th>
                    <th>Total Clicks</th>
                    <th>Last Delivery</th>
                    <th>Last Click</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r, i) => (
                    <tr key={i}>
                      <td className="da-report-name">{r.report_name}</td>
                      <td>{r.sent}</td>
                      <td>{r.clicked}</td>
                      <td>
                        {r.bounced > 0 ? (
                          <span className="da-status-badge da-status-bounced">{r.bounced}</span>
                        ) : '0'}
                      </td>
                      <td>
                        <span className={`da-rate-badge ${r.sent > 0 && r.clicked / r.sent >= 0.5 ? 'da-rate-good' : 'da-rate-low'}`}>
                          {r.sent > 0 ? Math.round(r.clicked / r.sent * 100) : 0}%
                        </span>
                      </td>
                      <td>{r.total_clicks}</td>
                      <td className="da-date-cell">{formatTimeAgo(r.latest_delivery)}</td>
                      <td className="da-date-cell">{r.latest_click ? formatTimeAgo(r.latest_click) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    );
  };

  // ── All Deliveries Tab ──
  const renderDeliveries = () => (
    <div className="da-deliveries">
      <div className="da-controls da-controls-multi">
        <SearchBar
          value={searchTerm}
          onChange={(e) => { setSearchTerm(typeof e === 'string' ? e : e.target.value); }}
          placeholder="Search report name or recipient..."
        />
        <select
          className="da-filter-select"
          value={deliveryTypeFilter}
          onChange={(e) => setDeliveryTypeFilter(e.target.value)}
          aria-label="Delivery type"
        >
          <option value="">All Types</option>
          <option value="EMAIL">Email</option>
          <option value="SFTP">SFTP</option>
          <option value="PICKUP">Pickup</option>
          <option value="VIEWER_DOWNLOAD">Viewer Download</option>
          <option value="PROCESSING_ERROR">Processing Error</option>
        </select>
        <select
          className="da-filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Status"
        >
          <option value="">All Statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="bounced">Bounced</option>
          <option value="complained">Complained</option>
        </select>
      </div>
      {deliveries.length === 0 ? (
        <Card><p className="da-empty">No deliveries found</p></Card>
      ) : (
        <Card>
          <div className="da-table-wrapper">
            <table className="da-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Report</th>
                  <th>Type</th>
                  <th>Recipient</th>
                  <th>Status</th>
                  <th>Clicks</th>
                  <th>Unique IPs</th>
                  <th>First Click</th>
                  <th>Last Click</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d, i) => {
                  const uniqueIps = getUniqueIps(d);
                  const clickCount = d.click_count || 0;
                  const isExpanded = expandedDelivery === (d.delivery_id || i);
                  const possibleForward = uniqueIps.length > 1;
                  return (
                    <React.Fragment key={d.delivery_id || i}>
                      <tr className={possibleForward ? 'da-row-flagged' : ''}
                          onClick={() => clickCount > 0 && setExpandedDelivery(isExpanded ? null : (d.delivery_id || i))}
                          style={clickCount > 0 ? { cursor: 'pointer' } : {}}
                      >
                        <td className="da-date-cell">{formatDate(d.timestamp)}</td>
                        <td className="da-report-name">{d.report_name || '—'}</td>
                        <td>
                          <span className={`da-type-badge da-type-${(d.delivery_type || 'EMAIL').toLowerCase().replace('_', '-')}`}>
                            {d.delivery_type === 'SFTP' ? 'SFTP'
                              : d.delivery_type === 'SFTP_PICKUP' || d.delivery_type === 'sftp_pickup' ? 'Pickup'
                              : d.delivery_type === 'viewer_download' || d.delivery_type === 'VIEWER_DOWNLOAD' ? 'Viewer'
                              : d.delivery_type === 'PROCESSING_ERROR' ? 'Error'
                              : 'Email'}
                          </span>
                        </td>
                        <td>{d.recipient_email || '—'}</td>
                        <td>
                          <span className={`da-status-badge da-status-${(d.delivery_status || 'unknown').toLowerCase()}`}>
                            {d.delivery_status || 'unknown'}
                          </span>
                          {d.bounce_reason && (
                            <span className="da-bounce-reason" title={d.bounce_reason}>
                              {' '}({d.bounce_reason})
                            </span>
                          )}
                          {d.error_reason && (
                            <span className="da-bounce-reason" title={d.error_reason}>
                              {' '}({d.error_reason})
                            </span>
                          )}
                        </td>
                        <td className="da-click-count">
                          {clickCount > 0 ? (
                            <span className="da-has-clicks">
                              <MousePointerClick size={14} /> {clickCount}
                            </span>
                          ) : (
                            <span className="da-no-clicks">0</span>
                          )}
                        </td>
                        <td className="da-click-count">
                          {clickCount > 0 ? (
                            <span className={possibleForward ? 'da-ip-warning' : 'da-ip-ok'}>
                              <Globe size={14} /> {uniqueIps.length}
                              {possibleForward && <ShieldAlert size={14} title="Multiple IPs — possible forwarding" />}
                            </span>
                          ) : (
                            <span className="da-no-clicks">—</span>
                          )}
                        </td>
                        <td className="da-date-cell">{d.first_click_timestamp ? formatDate(d.first_click_timestamp) : '—'}</td>
                        <td className="da-date-cell">{d.last_click_timestamp ? formatDate(d.last_click_timestamp) : '—'}</td>
                      </tr>
                      {isExpanded && clickCount > 0 && (
                        <tr className="da-detail-row">
                          <td colSpan={9}>
                            <div className="da-ip-detail">
                              <div className="da-ip-detail-header">
                                <Globe size={16} />
                                <span>Click Details — {clickCount} click{clickCount !== 1 ? 's' : ''} from {uniqueIps.length} unique IP{uniqueIps.length !== 1 ? 's' : ''}</span>
                                {possibleForward && (
                                  <span className="da-forward-warning">
                                    <ShieldAlert size={14} /> Possible forwarding detected
                                  </span>
                                )}
                              </div>
                              <div className="da-ip-list">
                                {uniqueIps.map((ip, idx) => {
                                  const count = (d.client_ips || []).filter(x => x === ip).length;
                                  return (
                                    <div key={idx} className="da-ip-item">
                                      <code>{ip}</code>
                                      <span className="da-ip-count">{count} click{count !== 1 ? 's' : ''}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pagination.total_pages > 1 && (
            <div className="da-pagination">
              <span>Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)</span>
              <div className="da-page-buttons">
                <button disabled={pagination.page <= 1} onClick={() => fetchDeliveries(pagination.page - 1)}>Prev</button>
                <button disabled={pagination.page >= pagination.total_pages} onClick={() => fetchDeliveries(pagination.page + 1)}>Next</button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );

  // ── By Report Tab ──
  const renderByReport = () => {
    if (!stats) return null;
    const { reports } = stats;
    const filtered = searchTerm
      ? reports.filter(r => r.report_name.toLowerCase().includes(searchTerm.toLowerCase()))
      : reports;

    return (
      <div className="da-by-report">
        <div className="da-controls">
          <SearchBar
            value={searchTerm}
            onChange={(e) => setSearchTerm(typeof e === 'string' ? e : e.target.value)}
            placeholder="Search reports..."
          />
        </div>
        {filtered.length === 0 ? (
          <Card><p className="da-empty">No reports found</p></Card>
        ) : (
          <div className="da-accordion-list">
            {filtered.map((r, i) => {
              const isExpanded = expandedReport === r.report_name;
              const openRate = r.sent > 0 ? Math.round(r.clicked / r.sent * 100) : 0;
              return (
                <Card key={i} className={`da-accordion-card ${isExpanded ? 'da-expanded' : ''}`}>
                  <button
                    className="da-accordion-header"
                    onClick={() => setExpandedReport(isExpanded ? null : r.report_name)}
                    aria-expanded={isExpanded}
                  >
                    <div className="da-accordion-title">
                      <FileText size={18} />
                      <span>{r.report_name}</span>
                    </div>
                    <div className="da-accordion-meta">
                      <span className="da-meta-pill"><Send size={14} /> {r.sent}</span>
                      <span className="da-meta-pill"><MousePointerClick size={14} /> {r.clicked}</span>
                      <span className={`da-rate-badge ${openRate >= 50 ? 'da-rate-good' : 'da-rate-low'}`}>
                        {openRate}%
                      </span>
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="da-accordion-body">
                      <div className="da-mini-stats">
                        <div className="da-mini-stat">
                          <span className="da-mini-label">Total Clicks</span>
                          <span className="da-mini-value">{r.total_clicks}</span>
                        </div>
                        {r.bounced > 0 && (
                          <div className="da-mini-stat">
                            <span className="da-mini-label">Bounced</span>
                            <span className="da-mini-value" style={{ color: '#e53e3e' }}>{r.bounced}</span>
                          </div>
                        )}
                        <div className="da-mini-stat">
                          <span className="da-mini-label">Last Delivery</span>
                          <span className="da-mini-value">{formatDate(r.latest_delivery)}</span>
                        </div>
                        <div className="da-mini-stat">
                          <span className="da-mini-label">Last Click</span>
                          <span className="da-mini-value">{r.latest_click ? formatDate(r.latest_click) : 'Never'}</span>
                        </div>
                      </div>
                      <div className="da-progress-bar da-progress-sm">
                        <div className="da-progress-fill" style={{ width: `${Math.min(openRate, 100)}%` }} />
                      </div>
                      <p className="da-progress-label">{r.clicked} of {r.sent} recipients clicked</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── By Recipient Tab ──
  const renderByRecipient = () => {
    if (!stats) return null;
    const { top_recipients } = stats;
    const filtered = searchTerm
      ? top_recipients.filter(r => r.email.toLowerCase().includes(searchTerm.toLowerCase()))
      : top_recipients;

    return (
      <div className="da-by-recipient">
        <div className="da-controls">
          <SearchBar
            value={searchTerm}
            onChange={(e) => setSearchTerm(typeof e === 'string' ? e : e.target.value)}
            placeholder="Search recipients..."
          />
        </div>
        {filtered.length === 0 ? (
          <Card><p className="da-empty">No recipients found</p></Card>
        ) : (
          <div className="da-accordion-list">
            {filtered.map((r, i) => {
              const isExpanded = expandedRecipient === r.email;
              const openRate = r.received > 0 ? Math.round(r.clicked / r.received * 100) : 0;
              return (
                <Card key={i} className={`da-accordion-card ${isExpanded ? 'da-expanded' : ''}`}>
                  <button
                    className="da-accordion-header"
                    onClick={() => setExpandedRecipient(isExpanded ? null : r.email)}
                    aria-expanded={isExpanded}
                  >
                    <div className="da-accordion-title">
                      <Mail size={18} />
                      <span>{r.email}</span>
                    </div>
                    <div className="da-accordion-meta">
                      <span className="da-meta-pill"><Send size={14} /> {r.received}</span>
                      <span className="da-meta-pill"><MousePointerClick size={14} /> {r.total_clicks}</span>
                      <span className={`da-rate-badge ${openRate >= 50 ? 'da-rate-good' : 'da-rate-low'}`}>
                        {openRate}%
                      </span>
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="da-accordion-body">
                      <div className="da-mini-stats">
                        <div className="da-mini-stat">
                          <span className="da-mini-label">Reports Received</span>
                          <span className="da-mini-value">{r.received}</span>
                        </div>
                        <div className="da-mini-stat">
                          <span className="da-mini-label">Reports Clicked</span>
                          <span className="da-mini-value">{r.clicked}</span>
                        </div>
                        {r.bounced > 0 && (
                          <div className="da-mini-stat">
                            <span className="da-mini-label">Bounced</span>
                            <span className="da-mini-value" style={{ color: '#e53e3e' }}>{r.bounced}</span>
                          </div>
                        )}
                        <div className="da-mini-stat">
                          <span className="da-mini-label">Total Clicks</span>
                          <span className="da-mini-value">{r.total_clicks}</span>
                        </div>
                      </div>
                      <div className="da-progress-bar da-progress-sm">
                        <div className="da-progress-fill" style={{ width: `${Math.min(openRate, 100)}%` }} />
                      </div>
                      <p className="da-progress-label">{r.clicked} of {r.received} reports opened</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ── Main Render ──
  return (
    <div className="delivery-audit-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title"><BarChart3 size={32} /> Delivery Audit</h1>
            <p className="page-subtitle">Track report deliveries, clicks, and engagement</p>
          </div>
          <div className="da-header-actions">
            <select
              className="da-days-select"
              value={daysBack}
              onChange={(e) => setDaysBack(Number(e.target.value))}
              aria-label="Time range"
            >
              {DAYS_OPTIONS.map(d => (
                <option key={d} value={d}>Last {d} {d === 1 ? 'day' : 'days'}</option>
              ))}
            </select>
            <button className="da-refresh-btn" onClick={handleRefresh} title="Refresh data">
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="da-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`da-tab ${activeTab === tab.key ? 'da-tab-active' : ''}`}
            onClick={() => { setActiveTab(tab.key); setSearchTerm(''); setStatusFilter(''); setDeliveryTypeFilter(''); }}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="da-content">
        {loading && <LoadingSpinner />}
        {error && <ErrorMessage message={error} onRetry={handleRefresh} />}
        {!loading && !error && (
          <>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'deliveries' && renderDeliveries()}
            {activeTab === 'reports' && renderByReport()}
            {activeTab === 'recipients' && renderByRecipient()}
          </>
        )}
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={5000} />}
    </div>
  );
}
